import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { Platform } from '@ionic/angular';
import { App } from '@capacitor/app';
import { environment } from 'src/environments/environment';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {

  private lastNonModalScreen: string = '';

  private readonly MODAL_PATHS = new Set([
    '/consumable',
    '/custom-form',
    '/modal/messages',
  ]);

  private readonly MODAL_NAMES: Record<string, string> = {
    '/consumable':     'Consumables',
    '/custom-form':    'Custom Form',
    '/modal/messages': 'Messages',
  };

  private readonly SCREEN_NAMES: Record<string, string> = {
    '/home':                                    'Mobile App | Guest - Home',
    '/login':                                   'Mobile App | Guest - Login',
    '/signup':                                  'Mobile App | Guest - Sign Up',
    '/password':                                'Mobile App | Guest - Password Reset',
    '/reset':                                   'Mobile App | Guest - Reset Password',
    '/verify':                                  'Mobile App | Guest - Verify',
    '/auth/home':                               'Mobile App | Auth - Home',
    '/auth/account':                            'Mobile App | Auth - Account - View',
    '/auth/account/update':                     'Mobile App | Auth - Account - Update',
    '/auth/account/change-password':            'Mobile App | Auth - Account - Change Password',
    '/auth/account/delete':                     'Mobile App | Auth - Account - Delete',
    '/auth/company':                            'Mobile App | Auth - Company',
    '/auth/settings':                           'Mobile App | Auth - Settings',
    '/auth/news-training':                      'Mobile App | Auth - News & Training',
    '/auth/onboard-equipment':                  'Mobile App | Auth - Onboard Equipment',
    '/auth/route/index':                        'Mobile App | Auth - Routes - List',
    '/auth/route/show':                         'Mobile App | Auth - Routes - Details',
    '/auth/route/others/index':                 'Mobile App | Auth - Routes (Others) - List',
    '/auth/route/others/show':                  'Mobile App | Auth - Routes (Others) - Details',
    '/auth/site/show':                          'Mobile App | Auth - Site - Details',
    '/auth/site/others/show':                   'Mobile App | Auth - Site (Others) - Details',
    '/auth/site-performance/show':              'Mobile App | Auth - Site Performance - View',
    '/auth/site-performance/store':             'Mobile App | Auth - Site Performance - Store',
    '/auth/site-performance/others/show':       'Mobile App | Auth - Site Performance (Others) - View',
    '/auth/history/shift/index':                'Mobile App | Auth - History - Shifts - List',
    '/auth/history/shift/show':                 'Mobile App | Auth - History - Shifts - Details',
    '/auth/history/route-performance':          'Mobile App | Auth - History - Route Performance',
    '/auth/history/site-performance':           'Mobile App | Auth - History - Site Performance',
    '/modal/shift':                             'Mobile App | Shift',
    '/delete':                                  'Mobile App | Auth - Account - Delete',
  };

  constructor(private platform: Platform, private router: Router) {}

  startNavigationTracking(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects.split('?')[0];
        const normalized = this.normalizeRoute(url);
        const isKnown = this.MODAL_PATHS.has(normalized) || normalized in this.SCREEN_NAMES;
        if (!isKnown) { return; }
        if (this.MODAL_PATHS.has(normalized)) {
          const parentLabel = this.SCREEN_NAMES[this.lastNonModalScreen];
          const modalName = this.MODAL_NAMES[normalized];
          if (parentLabel && modalName) {
            this.trackScreen(normalized, `${parentLabel} - ${modalName}`);
          } else {
            this.trackScreen(normalized);
          }
        } else {
          this.lastNonModalScreen = normalized;
          this.trackScreen(normalized);
        }
      });
  }

  async identify(userId: string): Promise<void> {
    if (!environment.analytics) { return; }
    try {
      await FirebaseAnalytics.setUserId({ userId });
      const platform = this.getPlatform();
      await FirebaseAnalytics.setUserProperty({ name: 'platform', value: platform });
      const info = await App.getInfo().catch(() => null);
      if (info) {
        await FirebaseAnalytics.setUserProperty({ name: 'app_bundle', value: info.id });
        await FirebaseAnalytics.setUserProperty({ name: 'app_version', value: info.version });
      }
    } catch {}
  }

  trackScreen(screenName: string, nameOverride?: string): void {
    if (!environment.analytics) { return; }
    const label = nameOverride ?? this.SCREEN_NAMES[screenName] ?? screenName;
    FirebaseAnalytics.setScreenName({ screenName, nameOverride: label })
      .catch(err => console.error('[Analytics] setScreenName error:', err));
  }

  trackEvent(name: string, params?: { [key: string]: any }): void {
    if (!environment.analytics) { return; }
    FirebaseAnalytics.logEvent({
      name,
      params: { app_platform: this.getPlatform(), ...(params ?? {}) },
    }).catch(err => console.error('[Analytics] logEvent error:', err));
  }

  trackLogin(method: string = 'email'): void {
    this.trackEvent('login', { method });
  }

  trackLogout(): void {
    this.trackEvent('logout');
  }

  trackShiftStarted(shiftId?: number): void {
    this.trackEvent('shift_started', shiftId ? { shift_id: shiftId } : {});
  }

  trackShiftCompleted(shiftId?: number): void {
    this.trackEvent('shift_completed', shiftId ? { shift_id: shiftId } : {});
  }

  trackPhotoTaken(type: 'before' | 'after' | 'custom_form', sitePerformanceId?: number): void {
    this.trackEvent(`photo_taken_${type}`, {
      ...(sitePerformanceId ? { site_performance_id: sitePerformanceId } : {}),
    });
  }

  trackRouteEnded(routeId?: number, routePerformanceId?: number): void {
    this.trackEvent('route_ended', {
      ...(routeId ? { route_id: routeId } : {}),
      ...(routePerformanceId ? { route_performance_id: routePerformanceId } : {}),
    });
  }

  trackSitePerformanceEnded(siteId?: number, sitePerformanceId?: number): void {
    this.trackEvent('site_performance_ended', {
      ...(siteId ? { site_id: siteId } : {}),
      ...(sitePerformanceId ? { site_performance_id: sitePerformanceId } : {}),
    });
  }

  trackRouteStarted(routeId?: number, routePerformanceId?: number): void {
    this.trackEvent('route_started', {
      ...(routeId ? { route_id: routeId } : {}),
      ...(routePerformanceId ? { route_performance_id: routePerformanceId } : {}),
    });
  }

  trackSitePerformanceStarted(siteId?: number, sitePerformanceId?: number): void {
    this.trackEvent('site_performance_started', {
      ...(siteId ? { site_id: siteId } : {}),
      ...(sitePerformanceId ? { site_performance_id: sitePerformanceId } : {}),
    });
  }

  private getPlatform(): string {
    if (this.platform.is('ios')) { return 'ios'; }
    if (this.platform.is('android')) { return 'android'; }
    return 'web';
  }

  private normalizeRoute(url: string): string {
    return url.replace(/\/\d+/g, '/:id');
  }
}
